import { Text, View, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { screens } from '@chillout/core'
import { Button, TextField } from '@chillout/ui'
import type { CardIconKey } from '@chillout/ui'
import { Numpad } from '../shared/numpad'
import { SpendBasedAmountDisplay } from './SpendBasedAmountDisplay'
import { StampPreview } from './StampPreview'
import type { StampMilestoneIcon } from './useStampSettings'

const MAX_AMOUNT_LENGTH = 6

interface SpendBasedInputProps {
  amount: string
  amountPerStamp: number
  onAmountChange: (amount: string) => void
  currentStamps?: number
  stampTarget: number
  maxStampsPerTransaction: number
  completionCardIcon: CardIconKey
  milestones: StampMilestoneIcon[]
  receiptReferenceEnabled: boolean
  receiptReference: string
  onReceiptReferenceChange: (value: string) => void
  onAddStamps: () => void
}

export function SpendBasedInput({
  amount,
  amountPerStamp,
  onAmountChange,
  currentStamps,
  stampTarget,
  maxStampsPerTransaction,
  completionCardIcon,
  milestones,
  receiptReferenceEnabled,
  receiptReference,
  onReceiptReferenceChange,
  onAddStamps,
}: SpendBasedInputProps) {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const twoColumn = width >= screens.lg

  const rawStampsToAdd = Math.floor((Number(amount) || 0) / amountPerStamp)
  const stampsToAdd = Math.min(rawStampsToAdd, maxStampsPerTransaction)
  const isCapped = rawStampsToAdd > maxStampsPerTransaction
  const buttonLabel = stampsToAdd === 1
    ? t('stampAwarding.addStampsSingle')
    : t('stampAwarding.addStamps', { count: stampsToAdd })

  const handleDigit = (digit: string) => {
    if (amount.length >= MAX_AMOUNT_LENGTH) return
    onAmountChange(amount + digit)
  }

  const handleDelete = () => {
    onAmountChange(amount.slice(0, -1))
  }

  const handleDoubleZero = () => {
    if (!amount || amount.length + 2 > MAX_AMOUNT_LENGTH) return
    onAmountChange(amount + '00')
  }

  if (twoColumn) {
    return (
      <View style={{ flexDirection: 'row', gap: 32 }}>
        {/* Left column: amount display + stamp preview */}
        <View style={{ flex: 1 }} className="gap-3">
          <SpendBasedAmountDisplay amount={amount} stampsToAdd={stampsToAdd} amountPerStamp={amountPerStamp} twoColumn />
          {currentStamps !== undefined && (
            <View className="mt-auto">
              <StampPreview
                currentStamps={currentStamps}
                stampDelta={stampsToAdd}
                stampTarget={stampTarget}
                completionCardIcon={completionCardIcon}
                milestones={milestones}
              />
            </View>
          )}
        </View>

        {/* Right column: numpad + button */}
        <View style={{ flex: 1 }} className="gap-4">
          <Numpad onDigit={handleDigit} onDelete={handleDelete} doubleZero onDoubleZero={handleDoubleZero} />
          <View className="mt-auto gap-3">
            {receiptReferenceEnabled && (
              <TextField
                label={t('stampAwarding.receiptReference.label')}
                placeholder={t('stampAwarding.receiptReference.placeholder')}
                value={receiptReference}
                onChangeText={onReceiptReferenceChange}
                returnKeyType="done"
              />
            )}
            {isCapped && (
              <Text className="text-sm lg:text-base text-warning text-center">
                {t('stampAwarding.maxStampsCapped', { max: maxStampsPerTransaction })}
              </Text>
            )}
            <Button label={buttonLabel} onPress={onAddStamps} disabled={stampsToAdd === 0} className="py-8" />
          </View>
        </View>
      </View>
    )
  }

  return (
    <View className="gap-4">
      <SpendBasedAmountDisplay amount={amount} stampsToAdd={stampsToAdd} amountPerStamp={amountPerStamp} twoColumn={false} />
      <Numpad onDigit={handleDigit} onDelete={handleDelete} doubleZero onDoubleZero={handleDoubleZero} />
      {currentStamps !== undefined && (
        <StampPreview
          currentStamps={currentStamps}
          stampDelta={stampsToAdd}
          stampTarget={stampTarget}
          completionCardIcon={completionCardIcon}
          milestones={milestones}
        />
      )}
      {receiptReferenceEnabled && (
        <TextField
          label={t('stampAwarding.receiptReference.label')}
          placeholder={t('stampAwarding.receiptReference.placeholder')}
          value={receiptReference}
          onChangeText={onReceiptReferenceChange}
          returnKeyType="done"
        />
      )}
      {isCapped && (
        <Text className="text-sm text-warning">
          {t('stampAwarding.maxStampsCapped', { max: maxStampsPerTransaction })}
        </Text>
      )}
      <Button label={buttonLabel} onPress={onAddStamps} disabled={stampsToAdd === 0} className="py-8" />
    </View>
  )
}